import { CheckCircleRounded, RotateRightRounded, WarningRounded } from '@mui/icons-material';
import { Box, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import React, { ReactElement, ReactNode } from 'react';
import { gray100, gray60, gray80, green50, red50 } from '../../../constants/colors';
import { ocrStatus } from '../../../constants/enum';

/** ステータスアイコンの共通スタイル（ホーム画面と一覧で統一） */
const iconStyles: Record<'success' | 'error' | 'processing', SxProps<Theme>> = {
  success: { fontSize: '24px', color: green50 },
  error: { fontSize: '24px', color: red50 },
  processing: { fontSize: '24px', color: gray60 },
};

/**
 * OCR読取ステータスを表すアイコン。
 * ホーム画面・OCR一覧で共通利用し、状態ごとの色付きRoundedアイコンを返す。
 */
export const OcrStatusIcon = ({ status }: { status: number }): ReactElement => {
  if (status === ocrStatus.error) {
    return <WarningRounded sx={iconStyles.error} />;
  }
  if (status === ocrStatus.completed) {
    return <CheckCircleRounded sx={iconStyles.success} />;
  }
  return <RotateRightRounded sx={iconStyles.processing} />;
};

const styles: Record<string, SxProps<Theme>> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: '1 1 0',
    minWidth: 0,
    alignItems: 'flex-start',
  },
  fileNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    maxWidth: '100%',
    minWidth: 0,
  },
  fileName: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '20px',
    color: gray100,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
    minWidth: 0,
  },
  status: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '16px',
    color: gray80,
    textAlign: 'left',
    // ホーム画面の長いエラー文言が省略されないよう折り返しを許可する
    // （OCR一覧のステータスは「読取完了/読取中/読取エラー」と短く折り返しは発生しない）
    maxWidth: '100%',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    maxWidth: '100%',
    minWidth: 0,
    // ステータス文言の行高（16px）を子パターンバッジの高さ（20px）に揃える。
    // 高さが違うまま中央揃えにすると、文言だけがバッジより上に寄って見える（#3314）
    '& > .MuiTypography-root': {
      lineHeight: '20px',
    },
  },
};

interface OcrFileNameStatusProps {
  /** ファイル名（1行目） */
  fileName: string;
  /** 読取ステータスの表示文言（2行目） */
  statusText: string;
  /** ステータス文言に付与するツールチップ（エラー時の案内など） */
  statusTooltip?: ReactNode;
  /** ファイル名の右に添えるバッジ等（「削除済み」など） */
  fileNameSuffix?: ReactNode;
  /**
   * ステータス文言の右に添えるバッジ等（子パターンなど。#3314）。
   * 「対応が必要」を示す削除済み・要注意は1行目に置き、
   * 「どう読んだか」を示す情報はステータスと同じ行に並べる
   */
  statusSuffix?: ReactNode;
}

/**
 * ファイル名（1行目）と読取ステータス（2行目）を縦2行で表示する共通コンポーネント。
 * ホーム画面の処理履歴とOCR一覧で見た目を統一するために利用する。
 */
export const OcrFileNameStatus = ({
  fileName,
  statusText,
  statusTooltip,
  fileNameSuffix,
  statusSuffix,
}: OcrFileNameStatusProps): ReactElement => (
  <Box sx={styles.root}>
    <Box sx={styles.fileNameRow}>
      <Tooltip title={fileName}>
        <Typography sx={styles.fileName}>{fileName}</Typography>
      </Tooltip>
      {fileNameSuffix}
    </Box>
    {/*
      ツールチップはステータス文言だけに掛ける。
      statusSuffix ごと包むと、バッジにもエラーの案内が出てしまう
    */}
    <Box sx={styles.statusRow}>
      {statusTooltip ? (
        <Tooltip title={statusTooltip}>
          <Typography sx={{ ...styles.status, cursor: 'help' }}>{statusText}</Typography>
        </Tooltip>
      ) : (
        <Typography sx={styles.status}>{statusText}</Typography>
      )}
      {statusSuffix}
    </Box>
  </Box>
);
